import { cloneElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { media } from '@/theme/media';

/** Props the Popover injects into the cloned trigger element. */
interface TriggerInjected {
  onClick?: (e: ReactMouseEvent<HTMLElement>) => void;
  'aria-haspopup'?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  'aria-expanded'?: boolean;
}

export interface PopoverProps {
  className?: string;
  /** A single React element that will be cloned to receive onClick + aria-haspopup + aria-expanded. */
  trigger: ReactElement<TriggerInjected>;
  children: ReactNode;
  /** Controlled open state (optional; uncontrolled if omitted). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Vertical placement of the panel relative to the trigger.
   * - 'bottom' (today's behaviour): always opens downward.
   * - 'top': always opens upward.
   * - 'auto' (default): opens downward unless there isn't room in the viewport below the
   *   trigger but there is above, in which case it flips upward. Panel positioning stays
   *   `position: absolute` inside the trigger's own container (no portal) — this only
   *   toggles which edge it hangs from.
   */
  placement?: 'auto' | 'bottom' | 'top';
  /**
   * Horizontal alignment of the panel relative to the trigger.
   * - 'end' (default, today's behaviour): the panel's right edge aligns with the
   *   trigger's right edge and the panel grows leftward.
   * - 'start': the panel's left edge aligns with the trigger's left edge and the
   *   panel grows rightward. Use when the trigger sits near the left edge of its
   *   column and a leftward panel would hang over an unrelated region (e.g. the
   *   shell rail, which can also paint OVER the panel from a different stacking
   *   subtree — keeping the panel inside its own column sidesteps that entirely).
   */
  align?: 'start' | 'end';
}

/** Gap between trigger and panel, in px. Single source of truth: shared by both the CSS
 *  offset and the fits-below/fits-above math, so changing it can't silently drift them apart. */
const PANEL_GAP = 6;

const Root = styled.div`
  position: relative;
  display: inline-block;
`;

const Panel = styled.div<{ $placement: 'top' | 'bottom'; $align: 'start' | 'end' }>`
  position: absolute;
  z-index: 50;
  ${({ $placement }) =>
    $placement === 'top'
      ? `bottom: calc(100% + ${PANEL_GAP}px);`
      : `top: calc(100% + ${PANEL_GAP}px);`}
  ${({ $align }) => ($align === 'start' ? 'left: 0;' : 'right: 0;')}
  min-width: 180px;
  /* Dropdown menus stay OPAQUE regardless of the glass-cards pref: a translucent
     menu over the animated backdrop is hard to read and the hover states get
     confusing. surfaceRaised gives an elevated, solid, always-legible surface. */
  background: ${({ theme }) => theme.color.surfaceRaised};
  background-image: ${({ theme }) => theme.surfaceGradients.overlay};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.elevation.md};
  padding: 6px;
  ${media.mobile`
    max-width: calc(100vw - 24px);
  `}
`;

export function Popover({
  className,
  trigger,
  children,
  open: controlled,
  onOpenChange,
  placement = 'auto',
  align = 'end',
}: PopoverProps) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlled ?? uncontrolled;
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Holds ONLY the measured result for placement='auto'. When placement is forced ('top' or
  // 'bottom'), this state is never written — the effective placement is derived at render time
  // instead, so the layout effect has no setState to run on that path.
  const [autoPlacement, setAutoPlacement] = useState<'top' | 'bottom'>('bottom');
  const resolvedPlacement: 'top' | 'bottom' =
    placement === 'auto' ? autoPlacement : placement === 'top' ? 'top' : 'bottom';

  function setOpen(next: boolean) {
    if (controlled === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  }

  /** Find the trigger's DOM element within Root for focus return. */
  function getTriggerEl(): HTMLElement | null {
    if (!rootRef.current) return null;
    // The trigger is the first direct child of Root (before the panel).
    const el = rootRef.current.firstElementChild;
    return el instanceof HTMLElement ? el : null;
  }

  /** Measure whether the panel fits below the trigger; flip above when it doesn't (and there's
   *  more room above). Runs synchronously via useLayoutEffect so the flip lands in the same
   *  paint as the panel's first render — no visible one-frame jump downward-then-up. */
  useLayoutEffect(() => {
    // Forced placements need no measurement — resolvedPlacement already derives them at render.
    if (placement !== 'auto') return;
    if (!open) return;

    function measure() {
      const triggerEl = getTriggerEl();
      const panelEl = panelRef.current;
      if (!triggerEl || !panelEl) return;
      const triggerRect = triggerEl.getBoundingClientRect();
      const panelHeight = panelEl.getBoundingClientRect().height;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const fitsBelow = spaceBelow >= panelHeight + PANEL_GAP;
      const fitsAbove = spaceAbove >= panelHeight + PANEL_GAP;
      setAutoPlacement(!fitsBelow && fitsAbove ? 'top' : 'bottom');
    }

    measure();
    window.addEventListener('resize', measure);
    // Placement is resolved once at open (and on resize), not continuously on scroll. The
    // decision reads triggerRect.bottom/top against window.innerHeight — both viewport-relative
    // and scroll-sensitive — so scrolling CAN make a resolved placement stale. We accept that:
    // popovers close on outside interaction (mousedown/Escape), so a stale placement is
    // short-lived, and re-measuring on every scroll event was judged not worth the cost here.
    return () => window.removeEventListener('resize', measure);
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;

    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        getTriggerEl()?.focus();
      }
    }

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const clonedTrigger = cloneElement(trigger, {
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
    onClick(e: ReactMouseEvent<HTMLElement>) {
      trigger.props.onClick?.(e);
      setOpen(!open);
    },
  });

  return (
    <Root ref={rootRef} className={className}>
      {clonedTrigger}
      {open && (
        <Panel
          ref={panelRef}
          role="menu"
          $placement={resolvedPlacement}
          $align={align}
          data-placement={resolvedPlacement}
        >
          {children}
        </Panel>
      )}
    </Root>
  );
}
